import { bangkokBusinessDay } from './businessDate';
import { dayIndexToMs, type DayRange, toDayIndex } from './day';

export type { DayRange } from './day';

export type PresetKey =
  'today' | '7d' | '30d' | 'thisMonth' | 'lastMonth' | 'custom';
export type FixedPreset = Exclude<PresetKey, 'custom'>;

export const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'วันนี้' },
  { key: '7d', label: '7 วัน' },
  { key: '30d', label: '30 วัน' },
  { key: 'thisMonth', label: 'เดือนนี้' },
  { key: 'lastMonth', label: 'เดือนก่อน' },
  { key: 'custom', label: 'กำหนดเอง' },
];

const firstDayOfMonth = (y: number, m: number) => toDayIndex(Date.UTC(y, m, 1));
const lastDayOfMonth = (y: number, m: number) =>
  toDayIndex(Date.UTC(y, m + 1, 0));

/** Resolve a fixed preset to a concrete inclusive Bangkok business-date range. */
export function presetRange(
  key: FixedPreset,
  todayDay = bangkokBusinessDay(),
): DayRange {
  const today = new Date(dayIndexToMs(todayDay));
  const y = today.getUTCFullYear(),
    m = today.getUTCMonth();
  switch (key) {
    case 'today':
      return { fromDay: todayDay, toDay: todayDay };
    case '7d':
      return { fromDay: todayDay - 6, toDay: todayDay };
    case '30d':
      return { fromDay: todayDay - 29, toDay: todayDay };
    case 'thisMonth':
      return { fromDay: firstDayOfMonth(y, m), toDay: todayDay };
    case 'lastMonth': {
      const pm = m === 0 ? 11 : m - 1,
        py = m === 0 ? y - 1 : y;
      return {
        fromDay: firstDayOfMonth(py, pm),
        toDay: lastDayOfMonth(py, pm),
      };
    }
  }
}

export const rangeLength = (r: DayRange): number => r.toDay - r.fromDay + 1;

/** The equal-length window immediately before `r` (for "เทียบงวดก่อน"). */
export function previousPeriod(r: DayRange): DayRange {
  const len = rangeLength(r);
  return { fromDay: r.fromDay - len, toDay: r.fromDay - 1 };
}

// ISO date <-> epoch-day (UTC) for the custom-range URL params
export const dayToIso = (day: number): string =>
  new Date(dayIndexToMs(day)).toISOString().slice(0, 10);
export const isoToDay = (iso: string): number =>
  toDayIndex(Date.parse(`${iso}T00:00:00Z`));

const TH_MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
];
const fmtDay = (day: number, withYear = false): string => {
  const d = new Date(dayIndexToMs(day));
  const s = `${d.getUTCDate()} ${TH_MONTHS[d.getUTCMonth()]}`;
  return withYear ? `${s} ${d.getUTCFullYear() + 543}` : s; // Buddhist year
};
/** Human label, e.g. "2 – 31 พ.ค. 2569" (Thai, Buddhist year). */
export function fmtRangeLabel(r: DayRange): string {
  return r.fromDay === r.toDay
    ? fmtDay(r.toDay, true)
    : `${fmtDay(r.fromDay)} – ${fmtDay(r.toDay, true)}`;
}
